/**
 * billingService — authFetch wrappers around /api/admin/ai/billing/*.
 *
 * WQ-234 (Phase 4.9 α8). Consumes endpoints shipped in WQ-233 + WQ-234:
 *   POST  /settle-period
 *   GET   /settlements
 *   GET   /settlements/count
 *   GET   /usage-aggregates              (WQ-234)
 *   GET   /usage-aggregates/count        (WQ-234)
 *
 * All endpoints require AI_ADMIN permission (operator-only).
 */

export interface UsageAggregate {
  id: string;
  allocation_id: string;
  tenant_id: string;
  capability_type: string;
  capability_id: string | null;
  period_start_at: string | null;
  period_end_at: string | null;
  counter_value: number;
  last_event_at: string | null;
  lifecycle_id: string | null;
  event_metadata: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface Settlement {
  id: string;
  tenant_id: string;
  capability_type: string;
  period_start: string | null;
  period_end: string | null;
  counter_value_snapshot: number;
  quota_snapshot: number | null;
  tier_snapshot: string | null;
  settlement_status: string | null;
  settled_at: string | null;
  settled_by_actor_id: string | null;
  settled_by_actor_type: string | null;
  settlement_metadata: Record<string, unknown>;
  created_at: string | null;
}

export interface UsageAggregateFilters {
  tenant_id?: string;
  capability_type?: string;
  period_start_after?: string;
  period_end_before?: string;
  limit?: number;
  offset?: number;
}

export interface SettlementFilters extends UsageAggregateFilters {
  status?: string;
}

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/ai/billing';

function buildQuery(filters: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') {
      params.set(k, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function listUsageAggregates(
  authFetch: AuthFetch,
  filters: UsageAggregateFilters = {},
): Promise<UsageAggregate[]> {
  const url = `${BASE}/usage-aggregates${buildQuery(filters as Record<string, string | number | undefined>)}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`listUsageAggregates failed: ${res.status}`);
  return res.json();
}

export async function countUsageAggregates(
  authFetch: AuthFetch,
  filters: UsageAggregateFilters = {},
): Promise<number> {
  const { limit: _l, offset: _o, ...rest } = filters;
  const url = `${BASE}/usage-aggregates/count${buildQuery(rest as Record<string, string | number | undefined>)}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`countUsageAggregates failed: ${res.status}`);
  const body = await res.json();
  return Number(body?.total ?? 0);
}

export async function listSettlements(
  authFetch: AuthFetch,
  filters: SettlementFilters = {},
): Promise<Settlement[]> {
  const url = `${BASE}/settlements${buildQuery(filters as Record<string, string | number | undefined>)}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`listSettlements failed: ${res.status}`);
  return res.json();
}

export async function countSettlements(
  authFetch: AuthFetch,
  filters: SettlementFilters = {},
): Promise<number> {
  const { limit: _l, offset: _o, ...rest } = filters;
  const url = `${BASE}/settlements/count${buildQuery(rest as Record<string, string | number | undefined>)}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(`countSettlements failed: ${res.status}`);
  const body = await res.json();
  return Number(body?.total ?? 0);
}
